import { useLifeAreaById, useUpdateLifeArea } from 'features/life-areas';
import { useUpdateUserSettings } from 'features/user/hooks/use-update-user-settings';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Check, Plus, SettingsSort18 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { IconButton } from 'shared/components/ui/icon-button';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuTitle,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { useOpenCreateGoalDialog } from 'shared/contexts/goal-form';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { PageEvents } from 'shared/services/tracking';
import { SortMenuOptions } from 'shared/types/sort-menu-options';
import { GoalSortingMode } from 'shared/types/sorting-mode';

import { GoalsOverview } from './components/goals-overview';
import * as Styled from './life-area-detail.style';

const sortOptions: SortMenuOptions<GoalSortingMode>[] = [
  {
    id: GoalSortingMode.Custom,
    value: GoalSortingMode.Custom,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Custom}`,
  },
  {
    id: GoalSortingMode.StartDate,
    value: GoalSortingMode.StartDate,
    label: `general.labels.goals.sort.options.${GoalSortingMode.StartDate}`,
  },
  {
    id: GoalSortingMode.Deadline,
    value: GoalSortingMode.Deadline,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Deadline}`,
  },
  {
    id: GoalSortingMode.Progress,
    value: GoalSortingMode.Progress,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Progress}`,
  },
];

export const Goals: React.FC = () => {
  const { t } = useTranslation();
  useTrackPageVisitEvent(PageEvents.LifeAreaDetailGoals, undefined);

  const { id } = useParams();
  const lifeArea = useLifeAreaById(id);
  const { submit: updateLifeArea } = useUpdateLifeArea();
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const interactionsRef = useRef<HTMLDivElement>(null);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

  const openCreateGoalDialog = useOpenCreateGoalDialog();

  const showCompletedGoals =
    user?.settings?.lifeAreaDetail?.goals?.showCompletedGoals ?? true;

  const onShowCompletedGoals = (value: boolean) => {
    updateUserSettings({
      lifeAreaDetail: {
        goals: {
          showCompletedGoals: value,
        },
      },
    });
  };

  const onAddGoal = () => {
    if (!lifeArea) {
      return;
    }
    openCreateGoalDialog({ lifeAreaId: lifeArea.id });
  };

  const onSort = useCallback(
    (value: GoalSortingMode) => {
      if (!lifeArea) {
        return;
      }
      updateLifeArea({ id: lifeArea.id, goalSortingMode: value });
      setIsSortMenuOpen(false);
    },
    [lifeArea, updateLifeArea],
  );

  if (!lifeArea) {
    return null;
  }

  return (
    <PageSectionBase
      title={t('pages.life-area-detail.sections.goals.label')}
      fullSize
      noPadding
      interactions={
        <Styled.InteractionsContainer ref={interactionsRef}>
          <IconButton
            icon={SettingsSort18}
            onClick={() => setIsSortMenuOpen(true)}
          />
          <IconButton icon={Plus} onClick={onAddGoal} />
          {isSortMenuOpen && (
            <PopupMenu
              referenceElement={interactionsRef}
              onOutside={() => setIsSortMenuOpen(false)}
              position="bottom-end"
            >
              <PopupMenuList hasBottomBorder>
                <PopupMenuListItem>
                  <PopupMenuToggle
                    label={t('general.labels.goals.show-completed-goals')}
                    checked={showCompletedGoals}
                    onChange={onShowCompletedGoals}
                  />
                </PopupMenuListItem>
              </PopupMenuList>
              <PopupMenuList>
                <PopupMenuTitle title={t('general.labels.goals.sort.title')} />
                {sortOptions.map(({ id: optionId, label, value }) => (
                  <PopupMenuListItem key={optionId}>
                    <PopupMenuButton
                      start={
                        (lifeArea.goalSortingMode ?? GoalSortingMode.Custom) ===
                        optionId ? (
                          <Icon icon={Check} />
                        ) : undefined
                      }
                      onClick={() => onSort(value)}
                      indentationLevel={
                        (lifeArea.goalSortingMode ?? GoalSortingMode.Custom) !==
                        optionId
                          ? 1
                          : 0
                      }
                    >
                      {t(label)}
                    </PopupMenuButton>
                  </PopupMenuListItem>
                ))}
              </PopupMenuList>
            </PopupMenu>
          )}
        </Styled.InteractionsContainer>
      }
    >
      <GoalsOverview
        lifeArea={lifeArea}
        hideCompleted={!showCompletedGoals}
        fullSize
      />
    </PageSectionBase>
  );
};
